import type { AxiosInstance } from "axios";
import type { CustomAxiosRequestConfig } from "./types";

export interface LoginExpiredContext {
  instance: AxiosInstance;
  getRefreshToken: () => string | null;
  clearAuth: () => void;
  fireUnauthorized: (message?: string) => void;
  shouldSkipRefresh: (url: string) => boolean;
  doRefresh: () => Promise<void>;
  getRefreshPromise: () => Promise<void> | null;
  setRefreshPromise: (promise: Promise<void> | null) => void;
}

type RequestConfig = CustomAxiosRequestConfig & { url?: string };

export type LoginExpiredPayload =
  | { config: RequestConfig; response?: unknown }
  | { config: RequestConfig; data?: unknown };

function runLoginExpiredLogic(
  requestConfig: RequestConfig,
  context: LoginExpiredContext,
  responseForReject?: unknown,
): Promise<unknown> {
  const {
    instance,
    getRefreshToken,
    clearAuth,
    fireUnauthorized,
    shouldSkipRefresh,
    doRefresh,
    getRefreshPromise,
    setRefreshPromise,
  } = context;

  const url = requestConfig.url ?? "";

  if (requestConfig.skipTokenRefresh === true || shouldSkipRefresh(url)) {
    clearAuth();
    fireUnauthorized("Login expired, please sign in again");
    return Promise.reject(
      Object.assign(new Error("Login expired"), {
        config: requestConfig,
        response: responseForReject,
      }),
    );
  }

  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    clearAuth();
    fireUnauthorized();
    return Promise.reject(
      Object.assign(new Error("No refresh token"), {
        config: requestConfig,
        response: responseForReject,
      }),
    );
  }

  let promise = getRefreshPromise();
  if (!promise) {
    promise = doRefresh().finally(() => setRefreshPromise(null));
    setRefreshPromise(promise);
  }

  return promise.then(() => instance.request(requestConfig));
}

export function createHandleLoginExpired(
  context: LoginExpiredContext,
): (payload: LoginExpiredPayload) => Promise<unknown> {
  return (payload: LoginExpiredPayload) => {
    const requestConfig = payload?.config as RequestConfig | undefined;
    if (!requestConfig) {
      return Promise.reject(
        Object.assign(new Error("Login expired"), {
          config: undefined,
          response:
            payload && "response" in payload ? payload.response : payload,
        }),
      );
    }

    const responseForReject =
      "response" in payload && payload.response !== undefined
        ? payload.response
        : payload;

    return runLoginExpiredLogic(requestConfig, context, responseForReject);
  };
}
